/**
 * Utility functions for getting tenant logo URLs
 */
import { getAssetPath } from "@/config/tenant";

/**
 * Get the URL for the tenant logo
 * @param {string} type - Type of logo ('logo', 'logoSmall', 'logoWhite')
 * @param {string} fallback - Fallback URL if tenant logo not found
 * @returns {string} - URL to the logo
 */
// Fallback is "" rather than "/w-logo.svg": that file does not exist in the
// build or on any tenant, so the old default produced a broken image (a 404
// that the SPA answers with HTML). Callers should render nothing when empty.
export function getTenantLogoUrl(type = "logo", fallback = "") {
  const tenantLogoPath = getAssetPath(type);
  return tenantLogoPath || fallback;
}

/**
 * Get the default tenant logo URL
 * @returns {string} - URL to the default logo
 */
export function getDefaultLogoUrl() {
  return getTenantLogoUrl("logo");
}

/**
 * Get the small tenant logo URL
 * @returns {string} - URL to the small logo
 */
export function getSmallLogoUrl() {
  return getTenantLogoUrl("logoSmall", getDefaultLogoUrl());
}

/**
 * Get the white tenant logo URL
 * @returns {string} - URL to the white logo
 */
export function getWhiteLogoUrl() {
  return getTenantLogoUrl("logoWhite", getDefaultLogoUrl());
}

/**
 * Replace getImgUrl for default images with tenant-aware version
 * @param {string} imageName - Image name
 * @returns {string} - URL to the image
 */
export function getTenantImageUrl(imageName) {
  // Handle special cases
  switch (imageName) {
    case "default-image.png":
    case "default-image.jpg":
      // Return tenant logo as default image
      return getDefaultLogoUrl();
    case "logo":
    case "logo.svg":
      return getDefaultLogoUrl();
    case "logo-small":
    case "small-logo.svg":
      return getSmallLogoUrl();
    case "logo-white":
    case "white-logo.svg":
      return getWhiteLogoUrl();
    default:
      // For other images, return the original path
      return `/img/${imageName}`;
  }
}
